import React from 'react';
import StarIcon from './StarIcon';

// Shared grouped-list table chrome. Owns the OUTER structure so both
// list pages (Briefs, Suppliers) consume one source of truth for:
//
//   - Summary row above the table ("Showing N across M groups
//     · Expand all / Collapse all").
//   - Table element (border-separate so sticky positioning works
//     under Tailwind's preflight; border-spacing-0 so cells touch
//     like collapse mode).
//   - Sticky thead container (the caller supplies the <th> cells via
//     the `thead` prop — column configs are too entity-specific to
//     fully abstract, but the sticky/z-layout/bg lives here).
//   - Sticky group-header rows under the thead — same chrome on every
//     group, every page: green left-accent bar (3px), collapse caret,
//     star icon (Pinned bucket only), label + count + meta.
//   - Row container styling — border-t border-line + hover bg,
//     shared so row dividers + hover treatment can't drift.
//
// Inner cell content is per-page (caller provides `renderRow(row)`).
// That's the deliberate seam: entity-specific cell rendering stays
// in the pages; everything else is shared.
//
// Props:
//   thead          ReactNode — caller's <th>...</th> cells (rendered
//                  inside a sticky <tr>). Caller is responsible for
//                  applying the shared header styles via PlainHeader
//                  / SortHeader components or matching markup.
//   colSpan        number — total column count, used by group-header
//                  td colSpan. Must match the thead column count.
//   groups         Array<[key, items[]]> | null — when null/empty,
//                  the table renders rows flat from the `rows` prop.
//                  When non-empty, rows render under group headers.
//   rows           Array<row> — flat row data when groups === null.
//   renderRow      (row, index?) => ReactNode — caller's row JSX.
//   getRowKey      (row) => string|number — React key for each row.
//   getGroupLabel  (key, items) => string — human-readable group
//                  label. Per-group, called once per render.
//   getGroupMeta   (key, items) => string|ReactNode — meta text after
//                  the count (e.g. "EARLIEST DEADLINE 04 Jun 2026" or
//                  "SORTED A–Z"). Caller computes this PER GROUP so
//                  the meta reflects the group's own contents — fixes
//                  the "same deadline on every group" bug flagged in
//                  the row-sync brief §5.
//   noun           string — "brief" / "supplier" — used in count copy.
//   collapsedGroups Set<key> — controlled state for which groups are
//                  collapsed. Caller owns this so auto-collapse logic
//                  (e.g. > 5 groups → collapse all) stays per-page.
//   onToggleGroup  (key) => void — toggle one group's collapsed state.
//   onCollapseAll  () => void — collapse every group.
//   onExpandAll    () => void — expand every group.
//   filteredCount  number — total filtered rows, used in summary copy.
//   getGroupAccent (key, items, groupBy) => string | null — optional
//                  per-group left accent override (e.g. red for
//                  unassigned-owner on Briefs). Defaults to green.
//   isPinnedGroup  (key) => boolean — whether to render the star icon
//                  on this group header.
//   summaryExtra   ReactNode | null — optional extra summary fragment
//                  appended after the "across N groups" count. Used by
//                  Briefs to highlight outlier groups in red (e.g.
//                  "3 unassigned"). Caller passes the raw JSX so the
//                  outlier styling stays per-page.
//   groupCountLabel string | null — optional override for the noun used
//                  in the "across N {x}" count copy. Briefs surfaces
//                  the active grouping ("projects" / "owners" /
//                  "statuses") instead of the generic "groups".

const DEFAULT_ACCENT = 'border-l-[#92D400]';

export default function GroupedListTable({
  thead,
  colSpan,
  groups,
  rows = [],
  renderRow,
  getRowKey = (row) => row?.id,
  getGroupLabel,
  getGroupMeta,
  noun = 'item',
  collapsedGroups,
  onToggleGroup,
  onCollapseAll,
  onExpandAll,
  filteredCount,
  getGroupAccent,
  isPinnedGroup = (key) => key === 'pinned',
  summaryExtra = null,
  groupCountLabel = null,
}) {
  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const totalGroups = hasGroups ? groups.length : 0;
  const allCollapsed = hasGroups && groups.every(([k]) => collapsedGroups?.has(k));
  const anyCollapsed = hasGroups && groups.some(([k]) => collapsedGroups?.has(k));

  return (
    <>
      {/* Summary row above the table — only when groups exist. Shows
          the total filtered count + group count, with Expand all /
          Collapse all controls. Identical across all list pages. */}
      {hasGroups && (
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <p className="font-['Metropolis'] text-xs text-[#51534A]">
            Showing <span className="text-fusion-mid-grey font-semibold">{filteredCount} {noun}{filteredCount === 1 ? '' : 's'}</span>
            {' across '}
            <span className="text-fusion-mid-grey font-semibold">
              {totalGroups} {groupCountLabel ? (totalGroups === 1 ? groupCountLabel.replace(/s$/, '') : groupCountLabel) : (totalGroups === 1 ? 'group' : 'groups')}
            </span>
            {summaryExtra}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onExpandAll}
              disabled={!anyCollapsed}
              className="fusion-field-label hover:text-active-green transition-colors disabled:opacity-30 disabled:hover:text-[#51534A]">
              Expand all
            </button>
            <button
              type="button"
              onClick={onCollapseAll}
              disabled={!!allCollapsed}
              className="fusion-field-label hover:text-active-green transition-colors disabled:opacity-30 disabled:hover:text-[#51534A]">
              Collapse all
            </button>
          </div>
        </div>
      )}

      {/* border-separate + border-spacing-0 is required for sticky
          to work on any table descendant (Tailwind's preflight sets
          collapse, which no-ops sticky). Cells use border-t (no
          border-b) to avoid double-borders between adjacent rows. */}
      <table className="w-full border-separate [border-spacing:0]">
        <thead>
          <tr className="sticky top-0 z-20">{thead}</tr>
        </thead>
        <tbody>
          {hasGroups
            ? groups.map(([key, items]) => {
                const collapsed = collapsedGroups?.has(key) || false;
                const label = getGroupLabel ? getGroupLabel(key, items) : key;
                const meta = getGroupMeta ? getGroupMeta(key, items) : null;
                const accent = (getGroupAccent && getGroupAccent(key, items)) || DEFAULT_ACCENT;
                const pinned = isPinnedGroup(key);
                return (
                  <React.Fragment key={`group-${key}`}>
                    {/* Group header — sticky just below the thead so it
                        persists when scrolling through a long group.
                        Green-tint bg, 3px left accent bar (per-group
                        colour via getGroupAccent), collapse caret,
                        star icon for the Pinned bucket, label + count
                        + meta. Same chrome on every group, every
                        page. */}
                    <tr className="bg-table-band border-y border-hair-admin sticky top-[44px] z-10">
                      <td colSpan={colSpan} className={`py-2.5 pl-3 pr-4 border-l-[3px] ${accent}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => onToggleGroup?.(key)}
                            className="text-[#51534A] hover:text-fusion-mid-grey transition-colors flex-shrink-0"
                            aria-label={collapsed ? 'Expand group' : 'Collapse group'}>
                            <span className="inline-block w-4 text-center text-[10px]">{collapsed ? '▶' : '▼'}</span>
                          </button>
                          {pinned && (
                            <span className="flex-shrink-0 inline-flex items-center" aria-hidden="true">
                              <StarIcon filled size={11} />
                            </span>
                          )}
                          <span className="font-['Metropolis'] text-[10px] font-extrabold tracking-[0.25em] uppercase text-fusion-forest">{label}</span>
                          {(items?.length != null || meta) && (
                            <span className="font-['Metropolis'] text-[9px] font-semibold tracking-[0.15em] uppercase text-[#51534A]">
                              {items?.length != null && `${items.length} ${noun}${items.length === 1 ? '' : 's'}`}
                              {meta && <> · {meta}</>}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {!collapsed && items.map((row, idx) => renderRow(row, idx, { groupKey: key }))}
                  </React.Fragment>
                );
              })
            : rows.map((row, idx) => renderRow(row, idx, { groupKey: null }))}
        </tbody>
      </table>
    </>
  );
}
